'use client';

import React from 'react';
import { motion, useInView, type Variants } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Typewriter hook
// ---------------------------------------------------------------------------

function useTypewriter(text: string, inView: boolean, speed = 55) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!inView || !text) return;

    setDisplayed('');
    setDone(false);

    let index = 0;
    const interval = setInterval(() => {
      index += 1;
      setDisplayed(text.slice(0, index));
      if (index >= text.length) {
        clearInterval(interval);
        setTimeout(() => setDone(true), 800);
      }
    }, speed);

    return () => clearInterval(interval);
  }, [inView, text, speed]);

  return { displayed, done };
}

// ---------------------------------------------------------------------------
// Extract text from React children (handles nested elements)
// ---------------------------------------------------------------------------

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return extractText(node.props.children);
  }
  return '';
}

// ---------------------------------------------------------------------------
// Framer Motion variants
// ---------------------------------------------------------------------------

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const wordVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 28,
    rotateX: -35,
    filter: 'blur(4px)',
  },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.65,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
};

const subtitleVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: 'easeOut' as const },
  },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SectionTitleProps {
  children: React.ReactNode;
  eyebrow?: string;
  subtitle?: string;
  className?: string;
  centered?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SectionTitle({
  children,
  eyebrow,
  subtitle,
  className,
  centered = true,
}: SectionTitleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);
  const inView = useInView(ref, { once: true, margin: '100px 0px' });
  const [isDrawn, setIsDrawn] = useState(false);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const titleText = extractText(children);
  const words = titleText.split(/\s+/).filter(Boolean);

  const { displayed, done } = useTypewriter(eyebrow ?? '', inView);

  const alignClass = centered ? 'items-center text-center' : 'items-start text-left';
  const lineMarginClass = centered ? 'mx-auto' : '';

  return (
    <div
      ref={ref}
      className={cn('flex flex-col gap-y-4', alignClass, className)}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Typewriter eyebrow                                                   */}
      {/* ------------------------------------------------------------------ */}
      {eyebrow && (
        <div className="flex items-center gap-x-[3px] h-[1.2em]">
          <span
            className="text-gold text-[10px] tracking-[0.35em] uppercase font-semibold"
            aria-label={eyebrow}
          >
            {displayed}
          </span>

          {/* Blinking cursor — fades out once typing is complete */}
          <motion.span
            className="inline-block w-[2px] h-[0.85em] bg-gold rounded-sm"
            animate={
              done
                ? { opacity: 0, transition: { duration: 0.3 } }
                : {
                    opacity: [1, 0, 1],
                    transition: { duration: 0.85, repeat: Infinity },
                  }
            }
            aria-hidden="true"
          />
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Staggered word reveal title                                          */}
      {/* ------------------------------------------------------------------ */}
      <motion.h2
        className="font-display text-4xl md:text-5xl font-semibold text-navy dark:text-white leading-[1.08] tracking-tight"
        style={{ perspective: 800 }}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        aria-label={titleText}
      >
        {words.map((word, i) => (
          <motion.span
            key={`${word}-${i}`}
            className="inline-block mr-[0.28em] last:mr-0"
            style={{ transformOrigin: 'bottom center' }}
            variants={wordVariants}
            aria-hidden="true"
          >
            {word}
          </motion.span>
        ))}
      </motion.h2>

      {/* ------------------------------------------------------------------ */}
      {/* Animated gold SVG line                                               */}
      {/* ------------------------------------------------------------------ */}
      <motion.svg
        width="80"
        height="6"
        viewBox="0 0 80 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('overflow-visible', lineMarginClass)}
        aria-hidden="true"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
      >
        {/* Draw animation line — replaced by pulse line once drawn */}
        {!isDrawn ? (
          <motion.line
            x1="0"
            y1="3"
            x2="80"
            y2="3"
            stroke="#D2A138"
            strokeWidth="2.5"
            strokeLinecap="round"
            variants={{
              hidden: { pathLength: 0, opacity: 0 },
              visible: {
                pathLength: 1,
                opacity: 1,
                transition: {
                  pathLength: { duration: 0.9, ease: 'easeInOut', delay: 0.4 },
                  opacity: { duration: 0.01 },
                },
              },
            }}
            onAnimationComplete={() => {
              if (inView && mountedRef.current) setIsDrawn(true);
            }}
          />
        ) : (
          <motion.line
            x1="0"
            y1="3"
            x2="80"
            y2="3"
            stroke="#D2A138"
            strokeWidth="2.5"
            strokeLinecap="round"
            animate={{
              opacity: [1, 0.55, 1],
            }}
            transition={{
              duration: 2.4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}
      </motion.svg>

      {/* ------------------------------------------------------------------ */}
      {/* Subtitle                                                             */}
      {/* ------------------------------------------------------------------ */}
      {subtitle && (
        <motion.p
          className="max-w-2xl text-base md:text-lg text-gray-500 dark:text-gray-400 leading-relaxed"
          variants={subtitleVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}
